import type { OpenAPI } from "simplydo/interfaces";

export type IdeaUpdatePayload = Partial<{
  name: OpenAPI.Schemas["Idea"]["name"];
  coverImage: OpenAPI.Schemas["Idea"]["coverImage"];
  templated: OpenAPI.Schemas["Idea"]["templated"];
  company: OpenAPI.Schemas["Idea"]["company"];
  ideaBusinessProfile: OpenAPI.Schemas["Idea"]["ideaBusinessProfile"];
  ideaBusinessProfileDetails: Record<string, unknown> | null;
}>;

/**
 * Insert `changes` at `path` within `obj`, spreading siblings so nested
 * siblings (e.g. other `templated` fields) are preserved.
 */
export const reducePath = <T>(
  obj: T,
  changes: unknown,
  path: string[],
): T | unknown => {
  if (path.length === 0) {
    return changes;
  }
  const item = path[0];
  if (item === undefined) {
    return changes;
  }
  const defaultObj = (obj ?? {}) as Record<string, unknown>;
  const nextObj = defaultObj[item] ?? {};

  return { ...defaultObj, [item]: reducePath(nextObj, changes, path.slice(1)) };
};

/**
 * Merge a path-based edit into a pending PUT payload without wiping sibling
 * keys already queued for save.
 */
export const mergeIdeaUpdatePayload = (
  pending: IdeaUpdatePayload,
  value: unknown,
  path: string[],
): IdeaUpdatePayload => reducePath(pending, value, path) as IdeaUpdatePayload;

const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

/**
 * Build a partial PUT body with only fields that differ between `from` and
 * `to`. Removed templated keys are sent as `null` so the API can `$unset`.
 */
export const diffIdeaUpdatePayload = (
  from:
    | Pick<OpenAPI.Schemas["Idea"], "templated" | "name" | "coverImage">
    | null
    | undefined,
  to:
    | Pick<OpenAPI.Schemas["Idea"], "templated" | "name" | "coverImage">
    | null
    | undefined,
): IdeaUpdatePayload => {
  const payload: IdeaUpdatePayload = {};
  if (!to) return payload;

  if (to.name !== undefined && to.name !== from?.name) {
    payload.name = to.name;
  }
  if (to.coverImage !== undefined && to.coverImage !== from?.coverImage) {
    payload.coverImage = to.coverImage;
  }

  const fromTemplated = from?.templated ?? {};
  const toTemplated = to.templated ?? {};
  const templated: Record<string, unknown> = {};
  const keys = new Set([
    ...Object.keys(fromTemplated),
    ...Object.keys(toTemplated),
  ]);
  for (const key of keys) {
    const nextValue = toTemplated[key];
    const prevValue = fromTemplated[key];
    if (!(key in toTemplated)) {
      if (key in fromTemplated) {
        templated[key] = null;
      }
      continue;
    }
    if (!valuesEqual(prevValue, nextValue)) {
      templated[key] = nextValue;
    }
  }
  if (Object.keys(templated).length > 0) {
    payload.templated = templated as OpenAPI.Schemas["Idea"]["templated"];
  }

  return payload;
};

/**
 * Shallow-merge two pending payloads, deep-merging known nested dict keys.
 */
export const mergePendingIdeaPayloads = (
  base: IdeaUpdatePayload,
  extra: IdeaUpdatePayload,
): IdeaUpdatePayload => {
  const nestedKeys = ["templated", "ideaBusinessProfileDetails"] as const;
  const merged: IdeaUpdatePayload = { ...base, ...extra };
  for (const key of nestedKeys) {
    const baseNested = base[key];
    const extraNested = extra[key];
    if (
      baseNested &&
      typeof baseNested === "object" &&
      extraNested &&
      typeof extraNested === "object"
    ) {
      merged[key] = {
        ...(baseNested as Record<string, unknown>),
        ...(extraNested as Record<string, unknown>),
      } as never;
    }
  }
  return merged;
};
